import { type Country, DEFAULT_COUNTRY } from "$lib/data/countries";
import { normalizeCompany, readUtmParams, validateCompany, validateEmail, validatePhone } from "$lib/utils/lead-validation";

export type LeadField = "email" | "company" | "phone";
export type LeadStatus = "idle" | "submitting" | "success" | "error";

/**
 * A lead being typed into a lead form, and its submission to /api/contact-lead, which forwards it to
 * the shared sheet. A field's error shows once the field has been left, or once a submit is tried.
 * @example
 * const lead = new LeadDraft();
 * // <input bind:value={lead.email} onblur={() => lead.touch("email")} />
 * // <form onsubmit={(e) => { e.preventDefault(); void lead.submit(); }}>
 */
export class LeadDraft {
	email = $state("");
	company = $state("");
	country = $state<Country>(DEFAULT_COUNTRY);
	/** The number without its dialling code: digits and spaces, at most 12 characters. */
	phone = $state("");
	status = $state<LeadStatus>("idle");
	#touched = $state({ email: false, company: false, phone: false });

	readonly fullPhone = $derived(`${this.country.dial} ${this.phone}`.trim());
	readonly issues = $derived({
		email: validateEmail(this.email),
		company: validateCompany(this.company),
		phone: validatePhone(this.phone.trim() ? this.fullPhone : ""),
	});
	/** Each field's issue once it shows, else null. */
	readonly errors = $derived({
		email: this.#touched.email ? this.issues.email : null,
		company: this.#touched.company ? this.issues.company : null,
		phone: this.#touched.phone ? this.issues.phone : null,
	});

	touch(field: LeadField) {
		this.#touched[field] = true;
	}

	/** Sets `phone` from typed text, keeping its digits and spaces; returns what was kept. */
	phoneSet(typed: string): string {
		this.phone = typed.replace(/[^0-9 ]/g, "").slice(0, 12);
		return this.phone;
	}

	async submit() {
		this.#touched = { email: true, company: true, phone: true };
		const { email, company, phone } = this.issues;
		if (email || company || phone || this.status === "submitting") return;
		this.status = "submitting";
		const response = await fetch("/api/contact-lead", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				email: this.email.trim(),
				company: normalizeCompany(this.company),
				phone: this.fullPhone,
				...readUtmParams(window.location.search),
			}),
		}).catch(() => null);
		this.status = response?.ok ? "success" : "error";
	}
}
